import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box, Chip, Divider, LinearProgress,
    Paper, TableBody, TableCell, TableHead, TableRow,
    TextField, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import ActionButton from "../../../../component/common/ActionButton";
import SearchField from "../../../../component/common/SearchField";
import ResizableTable from "../../../../component/common/ResizableTable";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import DateRangeRoundedIcon from "@mui/icons-material/DateRangeRounded";
import { financeApi, fmtKRWFull } from "../../api/financeApi";
import { ds } from "../../../../styles/designSystem";
import { today as getToday } from "../../../../util/dateUtils";
import { formatPercent } from "../../../../util/numberFormat";
import TablePaginator from "../../../../component/common/TablePaginator";
import LoadingDots from "../../../../component/common/LoadingDots";
import FinanceEntryModal    from "../../components/FinanceEntryModal";
import {
    ProjectCell, VendorCell, TransactionTitleCell, ApprovalLinkChip,
} from "../../components/FinanceLinkProvider";
import { useTableSort }     from "../../../../util/useTableSort";
import ComboBox from "../../../../component/common/ComboBox";

function lastDayOfMonth(year, month) {
    // KST 로컬 기준 말일. toISOString()(UTC 변환)은 +9 오프셋으로 말일이 하루 전으로 밀려
    // 매월 말일(7/31, 6/30 등) 거래가 목록·합계에서 누락되므로 로컬 getDate()로 계산한다.
    const day = new Date(year, month, 0).getDate();
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export default function TransactionTab({ departments = [], projectChannels = [], initialDeptId = null, initialMonth = null }) {
    const [rows, setRows] = useState([]);
    const [filter, setFilter] = useState({ type: "", deptId: initialDeptId || "" });

    // 대시보드/부서별 현황에서 팀 클릭으로 들어오면(deptId 딥링크) 그 팀으로 필터 동기화
    useEffect(() => {
        if (initialDeptId) setFilter(f => ({ ...f, deptId: String(initialDeptId) }));
    }, [initialDeptId]);

    // 키워드 검색 — 이 표는 서버 페이지네이션이라 클라이언트 필터(현재 페이지만 걸러짐)가 아니라
    // 서버 keyword 파라미터로 조회한다. 타자마다 요청하지 않도록 입력(keyword)→조회(keywordQuery) 디바운스.
    const [keyword, setKeyword] = useState("");
    const [keywordQuery, setKeywordQuery] = useState("");
    useEffect(() => {
        const t = setTimeout(() => setKeywordQuery(keyword.trim()), 300);
        return () => clearTimeout(t);
    }, [keyword]);

    // ── 날짜 필터 ──────────────────────────────
    const now = getToday();
    const [dateMode, setDateMode] = useState("monthly"); // "monthly" | "period"
    const [monthYear, setMonthYear] = useState(initialMonth || now.format("YYYY-MM"));
    const [periodFrom, setPeriodFrom] = useState(now.startOf("month").format("YYYY-MM-DD"));
    const [periodTo,   setPeriodTo]   = useState(now.endOf("month").format("YYYY-MM-DD"));

    // 부서별 현황/대시보드에서 월을 딥링크로 넘겨받으면 그 달·월별 모드로 동기화 (현재 월로 리셋 방지)
    useEffect(() => {
        if (initialMonth) { setDateMode("monthly"); setMonthYear(initialMonth); }
    }, [initialMonth]);

    const dateRange = useMemo(() => {
        if (dateMode === "monthly") {
            const [y, m] = monthYear.split("-").map(Number);
            return {
                from: `${y}-${String(m).padStart(2, "0")}-01`,
                to:   lastDayOfMonth(y, m),
            };
        }
        return { from: periodFrom, to: periodTo };
    }, [dateMode, monthYear, periodFrom, periodTo]);

    // 예산 요약 기준 월 — 월별 모드면 선택 월을, 기간별 모드면 이번달을 따른다
    const budgetYM = dateMode === "monthly" ? monthYear : now.format("YYYY-MM");
    const [budgetYear, budgetMonth] = budgetYM.split("-").map(Number);

    // ── 페이지네이션 (백엔드 처리) ─────────────
    const [page, setPage]         = useState(1);   // 1-based (UI)
    const [pageSize, setPageSize] = useState(20);
    const [totalPages, setTotalPages] = useState(1);
    const [totalElements, setTotalElements] = useState(0);

    // ── 정렬 (현재 페이지 결과 클라이언트 정렬) ──
    const { toggleSort, sortLabel, sortRows } = useTableSort("transactionDate", "desc");

    // ── 등록 폼 ───────────────────────────────
    const [entryOpen, setEntryOpen] = useState(false);   // 통합 등록 폼(확정 기본)
    const [accounts, setAccounts]   = useState([]);      // 확정 시 자금일보 반영 계좌
    const [deptBudget, setDeptBudget] = useState(null);

    // 수동 리로드 트리거 (저장/삭제/결재 처리 후 갱신용)
    const [reloadKey, setReloadKey]   = useState(0);
    const triggerReload = () => setReloadKey(k => k + 1);

    // 기간 요약 통계
    const [summary, setSummary] = useState(null);

    // 이전 필터 시그니처를 추적해 필터 변경 시 page 1로 리셋
    const filterSig = `${filter.type}|${filter.deptId}|${dateRange.from}|${dateRange.to}|${pageSize}|${keywordQuery}`;
    const prevSigRef = useRef(filterSig);
    const [listLoading, setListLoading] = useState(true);

    useEffect(() => {
        const filterChanged = prevSigRef.current !== filterSig;
        prevSigRef.current  = filterSig;

        const effectivePage = filterChanged ? 1 : page;
        if (filterChanged) setPage(1);

        const params = { page: effectivePage - 1, size: pageSize };
        if (filter.type)    params.type    = filter.type;
        if (filter.deptId)  params.deptId  = filter.deptId;
        if (dateRange.from) params.from    = dateRange.from;
        if (dateRange.to)   params.to      = dateRange.to;
        if (keywordQuery)   params.keyword = keywordQuery;

        setListLoading(true);
        financeApi.getTransactions(params)
            .then(data => {
                setRows(data?.content ?? []);
                setTotalPages(data?.totalPages ?? 1);
                setTotalElements(data?.totalElements ?? 0);
            })
            .catch(() => { setRows([]); setTotalPages(1); setTotalElements(0); })
            .finally(() => setListLoading(false));

        // 기간이 있을 때만 요약 조회
        if (dateRange.from && dateRange.to) {
            financeApi.getTransactionSummary(dateRange.from, dateRange.to)
                .then(setSummary)
                .catch(() => setSummary(null));
        }
    }, [page, filterSig, reloadKey]); // filterSig이 filter/dateRange/pageSize를 포함

    // 팀 선택 시 해당 월 예산 + 지출 요약 로드 (선택한 월 기준)
    useEffect(() => {
        if (!filter.deptId) { setDeptBudget(null); return; }
        financeApi.getBudgets(budgetYear, budgetMonth)
            .then(budgets => {
                const b = budgets.find(b => String(b.deptId) === String(filter.deptId));
                setDeptBudget(b || null);
            })
            .catch(() => setDeptBudget(null));
    }, [filter.deptId, budgetYear, budgetMonth]);

    // 확정 시 자금일보 반영 계좌 목록
    useEffect(() => { financeApi.getBankAccounts().then(d => setAccounts(Array.isArray(d) ? d : [])).catch(() => {}); }, []);

    // 선택 팀의 월 전체 수입/지출 집계 — 서버 요약(summary.teamStats) 기준.
    // 현재 페이지 rows 로 집계하면 거래가 페이지 크기(pageSize)를 넘을 때 현황 카드가
    // 과소 표시되므로, 월 전체 합계를 담은 teamStats 를 사용한다.
    const deptStat = (summary?.teamStats || []).find(t => String(t.deptId) === String(filter.deptId)) || null;
    const filteredIncome  = deptStat ? deptStat.income  : 0;
    const filteredExpense = deptStat ? deptStat.expense : 0;

    // 현재 페이지 결과를 클라이언트 정렬 (서버 페이지네이션은 그대로 유지)
    const sortedRows = useMemo(() => sortRows(rows, {
        // 금액은 수입/지출 부호로 정렬
        amount: r => r.type === "INCOME" ? (r.amount || 0) : -(r.amount || 0),
    }), [rows, sortRows]);

    return (
        <Box>
            {/* 필터 */}
            <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center", flexWrap: "wrap" }}>

                {/* 날짜 모드 토글 */}
                <ToggleButtonGroup
                    value={dateMode} exclusive size="small"
                    onChange={(_, v) => v && setDateMode(v)}
                    sx={{ height: 36 }}
                >
                    <ToggleButton value="monthly" sx={{ px: 1.5, fontSize: 12, gap: 0.5 }}>
                        <CalendarMonthRoundedIcon sx={{ fontSize: 14 }} /> 월별
                    </ToggleButton>
                    <ToggleButton value="period" sx={{ px: 1.5, fontSize: 12, gap: 0.5 }}>
                        <DateRangeRoundedIcon sx={{ fontSize: 14 }} /> 기간별
                    </ToggleButton>
                </ToggleButtonGroup>

                {/* 날짜 선택 */}
                {dateMode === "monthly" ? (
                    <TextField
                        size="small" type="month" value={monthYear}
                        onChange={e => setMonthYear(e.target.value)}
                        sx={{ width: 150 }}
                        inputProps={{ max: getToday().format("YYYY-MM") }}
                    />
                ) : (
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <TextField size="small" type="date" value={periodFrom}
                            onChange={e => {
                                const v = e.target.value;
                                setPeriodFrom(v);
                                if (v && periodTo && v > periodTo) setPeriodTo(v);
                            }}
                            sx={{ width: 148 }} />
                        <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>~</Typography>
                        <TextField size="small" type="date" value={periodTo}
                            onChange={e => {
                                const v = e.target.value;
                                setPeriodTo(v);
                                if (v && periodFrom && v < periodFrom) setPeriodFrom(v);
                            }}
                            sx={{ width: 148 }} />
                    </Box>
                )}

                <Divider orientation="vertical" flexItem />

                {/* 구분 필터 */}
                <Box sx={{ width: 120 }}>
                    <ComboBox placeholder="구분(전체)" clearable={false}
                        options={[{ value: "", label: "전체" }, { value: "INCOME", label: "수입" }, { value: "EXPENSE", label: "지출" }]}
                        value={filter.type}
                        onChange={v => setFilter(f => ({ ...f, type: v }))} />
                </Box>

                {/* 팀 필터 */}
                <Box sx={{ width: 150 }}>
                    <ComboBox placeholder="팀(전체)" clearable={false}
                        options={[{ value: "", label: "전체" }, ...departments.map(d => ({ value: d.deptId, label: d.deptName }))]}
                        value={filter.deptId}
                        onChange={v => setFilter(f => ({ ...f, deptId: v }))} />
                </Box>

                {/* 페이지당 N개 */}
                <Box sx={{ width: 100 }}>
                    <ComboBox clearable={false}
                        options={[20, 30, 50].map(n => ({ value: n, label: `${n}개` }))}
                        value={pageSize}
                        onChange={v => setPageSize(Number(v))} />
                </Box>

                {/* 검색 + 등록을 한 묶음으로 우측 정렬(ml:auto) — 줄바꿈돼도 항상 오른쪽 끝에 함께 붙고,
                    검색은 등록 버튼 바로 왼쪽에 위치한다. */}
                <Box sx={{ ml: "auto", display: "flex", gap: 2, alignItems: "center" }}>
                    {/* 키워드 검색 — 항목·프로젝트·거래처·카테고리·비고(서버 조회) */}
                    <SearchField value={keyword} onChange={setKeyword}
                        placeholder="항목·거래처·카테고리 검색" width={240} />
                    <ActionButton variant="add" onClick={() => setEntryOpen(true)} label="등록" />
                </Box>
            </Box>

            {/* 팀 선택 시 예산 요약 */}
            {filter.deptId && (
                <Paper sx={{ ...ds.cardPadded, mb: 2, border: 'none'}}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#2563eb" }}>
                            {/* 기간별 모드에서 수입/지출은 선택 기간 전체 합계이므로 제목도 그 기간을 그대로 보여준다(‘이번 달’ 오표기 제거). */}
                            {dateMode === "monthly"
                                ? `${budgetYear}년 ${budgetMonth}월`
                                : `${dateRange.from} ~ ${dateRange.to}`}
                            {" "}[{departments.find(d => String(d.deptId) === String(filter.deptId))?.deptName}] 현황
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: deptBudget ? 1.5 : 0 }}>
                        <Box>
                            <Typography sx={{ fontSize: 11, color: "#64748b" }}>수입</Typography>
                            <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#16a34a" }}>
                                +{fmtKRWFull(filteredIncome)}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: 11, color: "#64748b" }}>지출</Typography>
                            <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#dc2626" }}>
                                -{fmtKRWFull(filteredExpense)}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: 11, color: "#64748b" }}>순익</Typography>
                            <Typography sx={{ fontSize: 16, fontWeight: 700, color: "blue" }}>
                                {fmtKRWFull(filteredIncome-filteredExpense)}
                            </Typography>
                        </Box>
                        {deptBudget && (
                            <>
                                <Box>
                                    {/* 예산/잔여/사용률은 항상 '월' 기준(budgetYM)이라, 기간별 모드에선 어느 달 기준인지 명시해 수입/지출(기간)과 구분한다. */}
                                    <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                                        예산{dateMode === "period" ? ` (${budgetYear}.${budgetMonth} 기준)` : ""}
                                    </Typography>
                                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#2563eb" }}>
                                        {fmtKRWFull(deptBudget.budgetAmount)}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: 11, color: "#64748b" }}>잔여</Typography>
                                    <Typography sx={{ fontSize: 16, fontWeight: 700,
                                        color: deptBudget.remainingAmount < 0 ? "#dc2626" : "#0f172a" }}>
                                        {fmtKRWFull(deptBudget.remainingAmount)}
                                    </Typography>
                                </Box>
                            </>
                        )}
                    </Box>
                    {deptBudget && (() => {
                        const ratio = deptBudget.budgetAmount > 0
                            ? Math.min((deptBudget.usedAmount / deptBudget.budgetAmount) * 100, 100) : 0;
                        return (
                            <Box>
                                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                    <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                                        예산 사용률 ({fmtKRWFull(deptBudget.usedAmount)} / {fmtKRWFull(deptBudget.budgetAmount)})
                                    </Typography>
                                    <Typography sx={{ fontSize: 11, fontWeight: 700,
                                        color: ratio > 90 ? "#dc2626" : ratio > 70 ? "#d97706" : "#16a34a" }}>
                                        {formatPercent(ratio, 0)}
                                    </Typography>
                                </Box>
                                <LinearProgress variant="determinate" value={ratio} sx={{
                                    height: 6, borderRadius: 3, bgcolor: "#dbeafe",
                                    "& .MuiLinearProgress-bar": {
                                        borderRadius: 3,
                                        bgcolor: ratio > 90 ? "#dc2626" : ratio > 70 ? "#d97706" : "#2563eb",
                                    },
                                }} />
                            </Box>
                        );
                    })()}
                </Paper>
            )}


            <Paper sx={ds.card}>
                {listLoading && <LoadingDots />}
                {!listLoading && <ResizableTable size="small" sx={{
                    ...ds.tableSx,
                    // 아래 헤더 width(%) 는 ResizableTable 의 자연폭 측정 시 비율 시드로 쓰인다.
                    "& tbody td, & thead th": { px: 1, whiteSpace: "nowrap", textOverflow: "ellipsis" },
                }}>
                    <TableHead>
                        <TableRow>
                            <TableCell align="center" sx={{ width: "10.5%", cursor: "pointer" }} onClick={() => toggleSort("transactionDate")}>{sortLabel("transactionDate", "일자")}</TableCell>
                            <TableCell sx={{ width: "13.5%", cursor: "pointer" }} onClick={() => toggleSort("title")}>{sortLabel("title", "항목")}</TableCell>
                            <TableCell sx={{ width: "12%", cursor: "pointer" }} onClick={() => toggleSort("channelName")}>{sortLabel("channelName", "프로젝트")}</TableCell>
                            <TableCell sx={{ width: "12%", cursor: "pointer" }} onClick={() => toggleSort("vendorName")}>{sortLabel("vendorName", "거래처")}</TableCell>
                            <TableCell align="right" sx={{ width: "14%", cursor: "pointer" }} onClick={() => toggleSort("amount")}>{sortLabel("amount", "금액")}</TableCell>
                            <TableCell align="center" sx={{ width: "8%", cursor: "pointer" }} onClick={() => toggleSort("type")}>{sortLabel("type", "구분")}</TableCell>
                            <TableCell align="center" sx={{ width: "9%", cursor: "pointer" }} onClick={() => toggleSort("deptName")}>{sortLabel("deptName", "팀")}</TableCell>
                            <TableCell sx={{ width: "10%", cursor: "pointer" }} onClick={() => toggleSort("category")}>{sortLabel("category", "카테고리")}</TableCell>
                            <TableCell align="center" sx={{ width: "11%", cursor: "pointer" }} onClick={() => toggleSort("createdByName")}>{sortLabel("createdByName", "등록자")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedRows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 4, color: "#94a3b8" }}>
                                    해당 기간의 내역이 없습니다.
                                </TableCell>
                            </TableRow>
                        ) : sortedRows.map(row => (
                            <TableRow key={row.id}>
                                <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>{row.transactionDate}</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 500 }}>
                                    <TransactionTitleCell row={row} />
                                </TableCell>
                                <TableCell align="center">
                                    <ProjectCell channelId={row.channelId} channelName={row.channelName} />
                                </TableCell>
                                <TableCell align="center" sx={{ fontSize: 12 }}>
                                    <VendorCell vendorId={row.vendorId} vendorName={row.vendorName} />
                                </TableCell>
                                <TableCell align="right" sx={{
                                    fontWeight: 700,
                                    color: row.type === "INCOME" ? "#16a34a" : "#dc2626",
                                    whiteSpace: "nowrap",
                                }}>
                                    {row.type === "INCOME" ? "+" : "-"}{fmtKRWFull(row.amount)}
                                </TableCell>
                                <TableCell align="center">
                                    <Chip label={row.typeLabel} size="small" sx={{
                                        bgcolor: row.type === "INCOME" ? "#f0fdf4" : "#fef2f2",
                                        color: row.type === "INCOME" ? "#16a34a" : "#dc2626",
                                        fontWeight: 600,
                                    }} />
                                </TableCell>
                                <TableCell align="center">{row.deptName || "—"}</TableCell>
                                <TableCell align="center" sx={{ fontSize: 12, color: "#64748b" }}>
                                    {row.approvalId
                                        ? <ApprovalLinkChip row={row} />
                                        : (row.category || "—")}
                                </TableCell>
                                <TableCell align="center">{row.createdByName}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </ResizableTable>}
                {!listLoading && <TablePaginator
                    totalPages={totalPages}
                    page={page}
                    onPageChange={setPage}
                />}
            </Paper>

            {/* 통합 등록 폼 — 예정/확정 토글 (확정목록=확정 기본) */}
            <FinanceEntryModal open={entryOpen} onClose={() => setEntryOpen(false)} onSaved={triggerReload}
                mode0="COMPLETED" departments={departments} projectChannels={projectChannels} accounts={accounts} />

            {/*
              거래처·프로젝트·결재 상세 모달은 <FinanceLinkProvider/> 가 한 번만 마운트한다.
              여기서는 셀 컴포넌트(ProjectCell/VendorCell/TransactionTitleCell) 만 사용.
            */}
        </Box>
    );
}
